// /graph/order/:slug — the prerequisite closure of one node, in reading order.
//
// The shortest honest answer to "what do I have to read before this makes
// sense?" (plan §2.2). Same payload shape as /graph, so the same drawing works;
// the numbering is what differs, because here the order is the point.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "../components/PageShell";
import GraphCanvas from "../components/GraphCanvas";
import graphApi from "../services/graphApi";

export default function GraphOrderPage() {
  const { slug } = useParams();
  const [view, setView] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    graphApi
      .getNodeOrder(slug)
      .then((data) => {
        setView(data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  const flat = view ? view.layers.flatMap((l) => l.nodes) : [];
  const target = flat.find((n) => n.slug === slug);

  return (
    <PageShell>
      <Link
        to={`/graph/node/${slug}`}
        className="text-sm text-sky-400 transition hover:text-sky-300"
      >
        ← Back to the node
      </Link>

      {status === "loading" && <p className="mt-6 text-slate-500">Loading…</p>}
      {status === "error" && <p className="mt-6 text-rose-400">Node not found.</p>}

      {status === "ready" && view && (
        <>
          <header className="mb-8 mt-4">
            <h1 className="text-3xl font-bold tracking-tight text-slate-100">
              Reading path
            </h1>
            <p className="mt-2 text-slate-400">
              Everything <span className="text-slate-200">{target?.title || slug}</span>{" "}
              is built on, in an order that never puts a node before something it
              depends on — {flat.length}{" "}
              {flat.length === 1 ? "node" : "nodes"} in {view.layers.length}{" "}
              {view.layers.length === 1 ? "layer" : "layers"}.
            </p>
          </header>

          <GraphCanvas layers={view.layers} edges={view.edges} activeSlug={slug} />

          <ol className="mt-10 space-y-2">
            {flat.map((node, i) => (
              <li key={node.slug}>
                <Link
                  to={`/graph/node/${node.slug}`}
                  className={`group flex items-center gap-4 rounded-lg border px-4 py-3 transition ${
                    node.slug === slug
                      ? "border-slate-600 bg-slate-800/60"
                      : "border-slate-800 bg-slate-900/40 hover:border-sky-500/40 hover:bg-slate-900/80"
                  }`}
                >
                  <span className="w-6 shrink-0 text-right text-xs tabular-nums text-slate-500">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-sm text-slate-200 transition group-hover:text-sky-300">
                    {node.title}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </>
      )}
    </PageShell>
  );
}
